import { Container } from '@mui/material';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { ProcessStepper } from '@components/ProcessStepper/index';
import RecordCard from '@components/RecordCard';
import ViewTab from '@components/ViewTab';

import * as ROUTES from '@constants/routes';

import { useGetMedicalConditions } from '@hooks/api/medical-condition';

import {
  NoRecordsWrapper,
  Spacer,
  WrapperDiv,
} from '@layouts/Layouts/BigTitleLayout';

import { capitalize } from '@utils/format';
import { sortMedicalConditions } from '@utils/sort/records';

const messages = defineMessages({
  title: {
    id: 'medicalConditionsMain.title',
    defaultMessage: 'Medical Conditions',
  },
  diagnosed: {
    id: 'medicalConditionsMain.diagnosed',
    defaultMessage: 'Diagnosed',
  },
  noRecordsFound: {
    id: 'medicalConditionsMain.noRecordsFound',
    defaultMessage: 'No Known {status} Medical Conditions',
  },
  inactive: {
    id: 'medicalConditionsMain.inactive',
    defaultMessage: 'Inactive',
  },
  active: {
    id: 'medicalConditionsMain.active',
    defaultMessage: 'Active',
  },
});

const MedicalConditions: React.FC = () => {
  const { formatMessage } = useIntl();

  const { data, loading } = useGetMedicalConditions();

  const tabs = [
    { id: 'active', value: formatMessage(messages.active) },
    {
      id: 'inactive',
      value: formatMessage(messages.inactive),
    },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const tabData = data.filter((c) =>
    activeTab.id === 'active' ? c.isActive() : !c.isActive()
  );

  return (
    <ContentLoader loading={loading}>
      <Container maxWidth="sm">
        <WrapperDiv>
          <ViewTab
            changeTab={setActiveTab}
            selected={activeTab}
            tabArray={tabs}
          />
          <Spacer />
          <ProcessStepper
            allBlack
            stepNames={tabData.sort(sortMedicalConditions).map((c) => {
              const onset = c.getConditionOnset();
              return (
                <RecordCard
                  bottomText={
                    onset
                      ? `${formatMessage(
                          messages.diagnosed
                        )}: ${c.getConditionOnset()}`
                      : null
                  }
                  key={c.id}
                  noteDisplay={Boolean(c.recordNotes.length)}
                  onClick={() => {
                    navigate(`${ROUTES.MEDICAL_CONDITIONS}${c.id}`);
                  }}
                  recordSource={c.recordSource}
                  status={c.curationStatus}
                  text={c.condition}
                  viewed={c.viewedStatus}
                />
              );
            })}
          />

          {tabData.length === 0 && (
            <NoRecordsWrapper>
              {formatMessage(messages.noRecordsFound, {
                status: capitalize(activeTab.value),
              })}
            </NoRecordsWrapper>
          )}
        </WrapperDiv>
      </Container>
    </ContentLoader>
  );
};

export default withErrorBoundary(MedicalConditions, {
  FallbackComponent: ErrorBoundaryFallback,
});
